/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    FiArrowRight,
    FiArrowLeft,
    FiActivity,
    FiExternalLink
} from "react-icons/fi";
import PageContainer from "../components/PageContainer";
import { useUI } from "../theme/ThemeProvider";
import clsx from "clsx";

export default function ProductMovement() {
    const { itemId } = useParams();
    const navigate = useNavigate();
    const { theme, lang } = useUI();

    const isDark = theme === "dark";
    const isArabic = lang === "ar";

    const [movementData, setMovementData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [productName, setProductName] = useState("");

    const baseURL = (
        process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api"
    ).replace(/\/+$/, "");

    const token = localStorage.getItem("authToken");
    const tenantId = localStorage.getItem("tenantId");

    useEffect(() => {
        const fetchMovementData = async () => {
            try {
                setLoading(true);

                // Fetch product name first
                const productRes = await fetch(
                    `${baseURL}/GetItems?tenantId=${tenantId}&itemId=${itemId}`,
                    {
                        headers: {
                            Accept: "application/json",
                            Authorization: token ? `Bearer ${token}` : "",
                        },
                    }
                );

                if (productRes.ok) {
                    const productData = await productRes.json();
                    if (Array.isArray(productData) && productData.length > 0) {
                        setProductName(productData[0].itemName || "");
                    }
                }

                // Fetch orders
                const res = await fetch(
                    `${baseURL}/GetTenantOrders?tenantId=${tenantId}&statusId=-1`,
                    {
                        headers: {
                            Accept: "application/json",
                            Authorization: token ? `Bearer ${token}` : "",
                        },
                    }
                );

                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data)) {
                        const productOrders = [];
                        data.forEach(order => {
                            const items = order.details || [];
                            items.forEach(item => {
                                if (Number(item.itemId) === Number(itemId)) {
                                    productOrders.push({
                                        orderId: order.orderId,
                                        orderNo: order.orderNo,
                                        date: order.orderDate || order.createdAt,
                                        productValue: item.price,
                                        quantity: item.quantity,
                                        customerName: order.customerName,
                                        province: order.city || order.governorate || "—",
                                        variantName: item.variantName
                                    });
                                }
                            });
                        });
                        setMovementData(productOrders);
                    }
                }
            } catch (err) {
                console.error("fetchMovementData error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchMovementData();
    }, [itemId, baseURL, token, tenantId]);

    const parseDate = (dateStr) => {
        if (!dateStr) return "—";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString(isArabic ? 'ar-EG' : 'en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    return (
        <PageContainer title="" dir={isArabic ? "rtl" : "ltr"}>
            <div className="max-w-7xl mx-auto space-y-6 pb-20">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className={clsx(
                            "flex items-center justify-center w-10 h-10 rounded-full border transition-all active:scale-95",
                            isDark ? "border-gray-700 active:bg-gray-800 text-gray-300" : "border-gray-200 active:bg-gray-50 text-gray-600"
                        )}
                    >
                        {isArabic ? <FiArrowRight size={20} /> : <FiArrowLeft size={20} />}
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-purple-600/10 flex items-center justify-center text-purple-600">
                            <FiActivity size={24} />
                        </div>
                        <div>
                            <h1 className={clsx("text-2xl font-black", isDark ? "text-white" : "text-gray-900")}>
                                {isArabic ? "حركة المادة" : "Product Movement"}
                            </h1>
                            {productName && (
                                <p className="text-sm text-gray-400 font-medium mt-1">
                                    {productName}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className={clsx(
                    "rounded-3xl border overflow-hidden",
                    isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100"
                )}>
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                            <p className="font-bold opacity-60">{isArabic ? "جاري تحميل البيانات..." : "Loading movement data..."}</p>
                        </div>
                    ) : movementData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-50">
                            <FiActivity size={48} />
                            <p className="font-bold">{isArabic ? "لا توجد حركة مسجلة لهذه المادة" : "No movement recorded for this product"}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className={clsx(
                                        "text-gray-400 font-bold uppercase tracking-wider border-b",
                                        isDark ? "bg-[#0f172a] border-gray-800" : "bg-gray-50 border-gray-100"
                                    )}>
                                        <th className="py-4 px-4 text-center">{isArabic ? "تسلسل" : "#"}</th>
                                        <th className="py-4 px-4 text-center">{isArabic ? "رقم الطلب" : "Order No"}</th>
                                        <th className="py-4 px-4 text-center">{isArabic ? "تاريخ الطلب" : "Date"}</th>
                                        <th className="py-4 px-4 text-center">{isArabic ? "قيمة المنتج" : "Value"}</th>
                                        <th className="py-4 px-4 text-center">{isArabic ? "كمية المنتج" : "Quantity"}</th>
                                        <th className="py-4 px-4 text-center">{isArabic ? "اسم العميل" : "Customer"}</th>
                                        <th className="py-4 px-4 text-center">{isArabic ? "المحافظة" : "Province"}</th>
                                        <th className="py-4 px-4 text-center">{isArabic ? "تفاصيل الطلب" : "Details"}</th>
                                    </tr>
                                </thead>
                                <tbody className={clsx(
                                    "divide-y",
                                    isDark ? "divide-gray-800" : "divide-gray-100"
                                )}>
                                    {movementData.map((row, i) => (
                                        <tr key={i} className={clsx(
                                            "transition-colors",
                                            isDark ? "active:bg-gray-800/30" : "active:bg-gray-50/50"
                                        )}>
                                            <td className="py-4 px-4 text-center opacity-50 font-medium">{i + 1}</td>
                                            <td className="py-4 px-4 text-center font-bold text-purple-600">#{row.orderNo}</td>
                                            <td className="py-4 px-4 text-center font-medium">{parseDate(row.date)}</td>
                                            <td className="py-4 px-4 text-center font-bold">
                                                {Number(row.productValue).toLocaleString()} <span className="text-[10px] opacity-40">{isArabic ? "د.ع" : "IQD"}</span>
                                            </td>
                                            <td className="py-4 px-4 text-center">
                                                <span className="px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-600 font-bold text-xs">
                                                    x{row.quantity}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4 text-center font-medium">{row.customerName}</td>
                                            <td className="py-4 px-4 text-center">{row.province}</td>
                                            <td className="py-4 px-4 text-center">
                                                <button
                                                    onClick={() => navigate(`/admin-store/orders-details/${row.orderId}`)}
                                                    className="p-2 rounded-xl bg-purple-600 text-white transition-all shadow-md group inline-flex items-center justify-center"
                                                    title={isArabic ? "عرض تفاصيل الطلب" : "View Order Details"}
                                                >
                                                    <FiExternalLink size={16} className="transition-transform" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </PageContainer>
    );
}
